import type { StaticDashboard2StoredData } from '../types';

const STATIC_DASHBOARD_DB_NAME = 'syncly-static-dashboard';
const STATIC_DASHBOARD_STORE_NAME = 'pdf-export';
const STATIC_DASHBOARD_RECORD_KEY = 'latest';

const openStaticDashboardDb = () => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }

    const request = indexedDB.open(STATIC_DASHBOARD_DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STATIC_DASHBOARD_STORE_NAME)) {
        db.createObjectStore(STATIC_DASHBOARD_STORE_NAME);
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open IndexedDB'));
    };
  });
};

const runTransaction = <T>(
  mode: IDBTransactionMode,
  executor: (
    store: IDBObjectStore,
    resolve: (value: T) => void,
    reject: (reason?: unknown) => void,
  ) => void,
) => {
  return openStaticDashboardDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STATIC_DASHBOARD_STORE_NAME, mode);
        const store = transaction.objectStore(STATIC_DASHBOARD_STORE_NAME);

        transaction.oncomplete = () => {
          db.close();
        };

        transaction.onerror = () => {
          reject(transaction.error ?? new Error('IndexedDB transaction failed'));
        };

        transaction.onabort = () => {
          reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
        };

        executor(store, resolve, reject);
      }),
  );
};

export const isStaticDashboardStorageSupported = async () => {
  try {
    const db = await openStaticDashboardDb();
    db.close();
    return true;
  } catch {
    return false;
  }
};

export const saveStaticDashboardPayload = async (
  payload: StaticDashboard2StoredData,
) => {
  await runTransaction<void>('readwrite', (store, resolve, reject) => {
    const request = store.put(payload, STATIC_DASHBOARD_RECORD_KEY);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(request.error ?? new Error('Failed to save static dashboard payload'));
    };
  });
};

export const getStaticDashboardPayload = async () => {
  try {
    return await runTransaction<StaticDashboard2StoredData | null>(
      'readonly',
      (store, resolve, reject) => {
        const request = store.get(STATIC_DASHBOARD_RECORD_KEY);

        request.onsuccess = () => {
          resolve((request.result as StaticDashboard2StoredData | undefined) ?? null);
        };

        request.onerror = () => {
          reject(
            request.error ?? new Error('Failed to read static dashboard payload'),
          );
        };
      },
    );
  } catch (error) {
    console.error('Failed to read static dashboard payload from IndexedDB', error);
    return null;
  }
};
